import React from "react";

import { Client } from "@/Common/Api/Client";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { Frequency } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Frequencies";
import { Route } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Routes";
import { Unit } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Unit";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { PharmaNetContext, PharmaNetCredentials } from "@/Common/Components/Billing/PharmaNetCredentials";
import { HyperLinkControl } from "@/Common/Components/Buttons/HyperLinkControl";
import { ISortableItemRenderFunction } from "@/Common/Components/Collections/SortableItemContainer";
import { VigilanceIcon } from "@/Common/Components/IconControls/VigilanceIcon";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { GeneralInstructionsComponent } from "@/Common/Components/Patient/Prescription/GeneralInstructionsComponent";
import { SlideMenuBar } from "@/Common/Components/Sidebar/SlideMenuBar";
import { SpanControl } from "@/Common/Components/SpanControl";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { DateTime } from "@/Common/Helpers/DateTime";
import { EmployeeTitleHelper } from "@/Common/Helpers/EmployeeTitleHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { SecondScreenHelper } from "@/Common/Helpers/SecondScreenHelper";
import { EmployeeTitle } from "@/Common/Models/Employee/Enums/EmployeeTitle";
import { EmployeeType } from "@/Common/Models/Employee/Enums/EmployeeType";
import { Guid } from "@/Common/Models/Guid";
import { MyleTabKey } from "@/Common/Models/MyleMenu/enums/MyleTabKey";
import { Patient } from "@/Common/Models/Patient/Patient";
import { PrescriptionMedicationCheckBoxes, PrescriptionMedicationKendoComboBoxType, PrescriptionMedicationTextBoxes } from "@/Common/Models/Patient/PatientEnums";
import { Person } from "@/Common/Models/Person/Person";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { SortableItemUIModel } from "@/Common/Models/UI/SortableItemUIModel";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { SortableContainerForActiveMedication } from "@/Patient/Components/MedicalSummary/SortableContainerForActiveMedication";
import { MedicationSearchListItem, SearchMedication } from "@/Patient/Components/Medication/SearchMedication";
import { MedicalItemHelper } from "@/Patient/Helpers/MedicalItemHelper";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { VigilanceQueryViewerPopupHelper } from "@/Patient/Helpers/VigilanceQueryViewerPopupHelper";
import { IJPrescriptionTabModel } from "@/Patient/Models/JSONInterfaces/Medication/IJPrescriptionTabModel";
import { MedicalItemMedicationAndPrescriptionDosage } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescriptionDosage";
import { MedicationDosageDisplayMode, MedicationDosageDisplayModeDurationUnit, MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { PatientPrescriptionAdjustMedicationPopupModel } from "@/Patient/Models/PatientPrescriptionAdjustMedicationPopupModel";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { IPrescriptionTabModel } from "@/Patient/Models/UI/Medication/IPrescriptionTabModel";
import { MedicalReactionsView } from "../MedicalItems/MedicalReactionsView";
import { SummaryPanelButtons } from "../MedicalSummaryPanel/SummaryPanelButtons";
import { VitalSignsView } from "../VitalSigns/VitalSignsView";
import { ActiveMedicationList } from "./ActiveMedicationList";
import { DosageDurationOrQuantityEditForm } from "./DosageDurationOrQuantityEditForm";
import { DosageEditForm } from "./DosageEditForm";
import { IncreasedOrDecreasedMedicationText } from "./IncreasedOrDecreasedMedicationText";
import { LongTermMedicationIcon } from "./LongTermMedicationIcon";
import { MedicationEditForm } from "./MedicationEditForm";
import { PatientPrescriptionActiveMedicationConflicPopup } from "./PatientPrescriptionActiveMedicationConflicPopup";
import { PatientPrescriptionAdjustMedicationPopup } from "./PatientPrescriptionAdjustMedicationPopup";
import { VigilanceSanteAlert } from "./VigilanceSanteAlert";

import styles from "./PatientPrescriptionTab.module.scss";

import img_icon_addfavorite from "@/Globals/_images/icons/AddFavorite.png";
import img_icon_delete from "@/Globals/_images/icons/delete.png";
import img_icon_favorite from "@/Globals/_images/icons/Favorite.png";

interface IPatientPrescriptionTabProps {
    PatientContactId: string;
    MedicalSummaryModel: IPatientMedicalSummaryModel;
    Model: IPrescriptionTabModel;
    encounterSiteId: string;
    isAdjustButtonDisabled?: boolean;
    isRenewalButtonDisabled?: boolean;
    isMedicationClickDisabled?: boolean;
    isSearchedMedicationDisabled?: boolean;
    isGlobalRenewalNumberDisabled?: boolean;
    isAddFavoriteDisabled?: boolean;
    emptyRxDisabledCssName?: string;
    addFavoriteDisabledCssName?: string;
    shouldHeaderContainGlobalRenewalNumber?: boolean;
    onToggleSelectedMedicationList?: (selectedMedicationListType: MedicationListType) => void;
    onAdjustMedicationPopupOpen: (medicationId: string) => void;
    onRenewMedication?: (medicationId: string) => void;
    onRenewAllMedications?: () => void;
    onRemoveMedication?: (medication: MedicationModel) => void;
    onAddEmptyMedication: () => void;
    onVigilanceSanteIconClick?: () => void;
    onAddSelectedMedication: (item: MedicationSearchListItem) => void;
    onExternalSearchMedicationTextChange: (medicationName: string) => void;
    toggleAddAsFavorite: () => void;
    updateGlobalRenewalsNumberChange: (newText: string) => void;
    showPatientDetails: boolean;
    patientModel?: Patient;
}

class PatientPrescriptionTabState {
    public WithSignature: boolean;
    public IsEmptyPrescription: boolean;
    public selectedSupervisorName: string;
    public selectedSupervisorContactId: Guid | string;
    public isSupervisorLocked: boolean;
    public isPharmaNetLoginOpen: boolean;
    public isClickableTextPanelOpen: boolean;
    public generalInstructions: string;
    public isHeaderExpanded: boolean;
    public isDurationManuallyChangedByDosageId: { [id: string]: boolean } = {};
    public isLongTermManuallyChangedByMedicationId: { [id: string]: boolean } = {};
    public initialDurationInDaysByDosageId: { [id: string]: number } = {};
    public initialLongTermValueByMedicationId: { [id: string]: boolean } = {};
}

enum MedicationListType {
    Active,
    DSQ,
    Completed
}

class PatientPrescriptionTab extends BaseReactComponent<IPatientPrescriptionTabProps, PatientPrescriptionTabState> {
    private IsDSQLoading = false;
    private supervisors: Person[];
    private isResident: boolean = false;
    private subHeaderResizeObserver: ResizeObserver = new ResizeObserver(() => {
        $(window).trigger("resize");
    });
    private subHeaderRef: React.RefObject<HTMLDivElement> = React.createRef();

    public static GetDefaultPatientPrescriptionTabModel(): IJPrescriptionTabModel {
        var patientPrescriptionTabModel = {
            SearchText: "",
            SearchTextEventId: "",
            MedicationModels: [],
            DSQMedicationModels: null,
            EncounterId: "",
            IsLoading: false,
            ShowAdjustMedicationPopup: false,
            AdjustMedication: null,
            LeftPaneAllMedicationCheckbox: false,
            IsFavorite: false,
            prescriptionId: GuidHelper.NewGuid(),
            selectedMedicationList: MedicationListType.Active,
            isDSQLoading: false,
            lastAcceptedMyleActiveMedicationConflic: null,
            showMyleActiveMedicationConflicPopup: false,
            generalInstructions: null,
            isHeaderExpanded: screen.width >= 1440
        };
        return patientPrescriptionTabModel;
    }

    public render(): JSX.Element {
        const closeClickableTextPanel = (): void => {
            if (this.state.isClickableTextPanelOpen) this.setState({ ...this.state, isClickableTextPanelOpen: false });
        };

        return (
            <div className="prescriptionTab-container" onClick={() => closeClickableTextPanel()}>
                {this.props.showPatientDetails && (
                    <>
                        <div ref={this.subHeaderRef} className={`${styles.subHeader} ${this.state.isHeaderExpanded ? styles.expanded : ""}`}>
                            <div className={styles.vitalSign}>
                                <VitalSignsView
                                    currentPatient={this.props.patientModel ?? patientStore.PatientModels[this.props.PatientContactId]}
                                    cssClassName={styles.vitalSign}
                                    cssClassNameDate={styles.vitalSignDate}
                                    vitalSign={MedicalItemHelper.getLatestVitalSign(this.props.MedicalSummaryModel?.get("MedicalItemsVitalSign"))}
                                    showArrow={false}
                                />
                            </div>
                            <div className={`flex-row ${styles.patientDetailContainer}`}>
                                <MedicalReactionsView reactions={this.props.MedicalSummaryModel?.get("MedicalItemsMedicalReaction").toArray() ?? []} />
                                <GeneralInstructionsComponent
                                    generalInstructions={this.state.generalInstructions}
                                    isClickableTextPanelOpen={this.state.isClickableTextPanelOpen}
                                    onTextChangeCallback={(value: string) => this.setState({ ...this.state, generalInstructions: value })}
                                    onSaveFieldBlur={() => this.saveGeneralInstructions()}
                                    onCloseClickableText={() => closeClickableTextPanel()}
                                    onOpenClickableText={() => this.setState({ ...this.state, isClickableTextPanelOpen: true })}
                                    shouldTriggerResize
                                    isReadonly={!!this.props.Model?.get("MedicationModels")?.isEmpty()}
                                />
                                <div className={styles.linkContainer}>
                                    {cacheStore.ClinicSettings.EnableSecondScreen && sessionStore.getCurrentEmployee().Type !== EmployeeType.Assistant && (
                                        <div className={styles.summaryPanelButtonContainer} data-cy="prescriptionrenewalview-medication-viewsummary">
                                            <SummaryPanelButtons
                                                isMedicalSummaryPanelOpen={false}
                                                onPopoutClick={() => this.openSecondScreen()}
                                                onMedicalSummaryLinkClick={() => this.openSecondScreen()}
                                                cssClass="noteToggleContainer"
                                                hideOpenSummaryPanelLink
                                            />
                                        </div>
                                    )}
                                    {cacheStore.hasPharmaNetAccess() && (
                                        <div data-cy="prescriptionrenewalview-medication-pharmanet">
                                            <a
                                                onClick={() => this.setState({ ...this.state, isPharmaNetLoginOpen: true })}
                                            >
                                                {cacheStore.GetString("PharmaNet")}
                                            </a>
                                        </div>
                                    )}
                                    {this.state.isPharmaNetLoginOpen && (
                                        <PharmaNetCredentials
                                            onClose={() => this.setState({ ...this.state, isPharmaNetLoginOpen: false })}
                                            patient={this.props.patientModel ?? patientStore.PatientModels[this.props.PatientContactId]}
                                            context={PharmaNetContext.Patient}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div
                            className={styles.horizontalBarContainer}
                            onClick={() => this.setState(p => {
                                const toggledHeaderState = !p.isHeaderExpanded;
                                this.saveIsHeaderExpanded(toggledHeaderState);
                                return { ...p, isHeaderExpanded: toggledHeaderState };
                            })}
                        >
                            <SlideMenuBar />
                        </div>
                    </>
                )}
                {this.props.Model.get("ShowAdjustMedicationPopup") == true && (
                    <PatientPrescriptionAdjustMedicationPopup
                        DefaultSelectedCode="1"
                        MedicationBeingAdjusted={this.props.Model.get("AdjustMedication")}
                        onClosePopup={() => sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.AdjustMedicationPopup_Cancel)}
                        onSavePopup={(prescriptionAdjustMedicationPopupModel: PatientPrescriptionAdjustMedicationPopupModel) => {
                            window.setTimeout(() => {
                                sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.AdjustMedicationPopup_Continue, {
                                    item: prescriptionAdjustMedicationPopupModel
                                });
                            }, 100); // Make sure its the latest state;
                        }}
                        defaultDiscontinueReason={MedicationModel.getDefaultDiscontinueReason()}
                    />
                )}
                {this.props.Model.get("showMyleActiveMedicationConflicPopup") == true &&
                    <PatientPrescriptionActiveMedicationConflicPopup PatientContactId={this.props.PatientContactId} MedicationsToPrescribe={this.props.Model.get("MedicationModels")} onCloseOperation={LocalPatientOperations.PrescriptionTab.CloseActiveMedConflicsPopup} />
                }
                <div className="tb">
                    <ActiveMedicationList
                        medicalSummary={this.props.MedicalSummaryModel}
                        prescription={this.props.Model}
                        isAdjustButtonDisabled={!!this.props.isAdjustButtonDisabled}
                        isRenewalButtonDisabled={!!this.props.isRenewalButtonDisabled}
                        isMedicationClickDisabled={!!this.props.isMedicationClickDisabled}
                        toggleSelectedMedication={medicationListType => this.toggleSelectedMedication(medicationListType)}
                        onAdjustMedicationPopupOpen={this.props.onAdjustMedicationPopupOpen}
                        onRenewMedication={this.props.onRenewMedication}
                        onRenewAllMedications={this.props.onRenewAllMedications}
                        onExternalSearchMedicationTextChange={this.props.onExternalSearchMedicationTextChange}
                    />
                    {this.rightSide()}
                </div>
            </div>
        );
    }

    public componentWillMount(): void {
        var state = new PatientPrescriptionTabState();
        state.WithSignature = true;
        state.IsEmptyPrescription = true;
        state.isSupervisorLocked = !IsNullOrWhiteSpace(sessionStore.lockedSupervisorContactId);
        if (!IsNullOrWhiteSpace(sessionStore.lockedSupervisorContactId)) {
            state.selectedSupervisorContactId = sessionStore.lockedSupervisorContactId;
            state.selectedSupervisorName = cacheStore.GetCliniciansPersonsByContactId(sessionStore.lockedSupervisorContactId).NameWithPrefix;
        }

        this.isResident = sessionStore.getCurrentEmployee().shouldHaveSupervisorBasedOnTitle();
        this.supervisors = cacheStore.Clinicians.filter(clinician => {
            if (clinician.EmployeeModel) {
                const title = Employee.getIssuingOrgRule(clinician.EmployeeModel)?.Title;

                return title
                    && (
                        EmployeeTitleHelper.isDoctor(title)
                        || title === EmployeeTitle.NursePractitioner
                        || EmployeeTitleHelper.isNurse(title)
                        || EmployeeTitleHelper.isProfessional(title)
                    );
            }
            return false;
        });

        this.setState(state);
    }

    public componentWillReceiveProps(nextProps: IPatientPrescriptionTabProps): void {
        this.mutateState(s => {
            const newModels = nextProps.Model.get("MedicationModels");

            s.IsEmptyPrescription = (newModels?.size ?? 0) < 1;

            if (!newModels) {
                s.initialLongTermValueByMedicationId = {};
                s.initialDurationInDaysByDosageId = {};
            }
            else {
                newModels.forEach(medication => {
                    s.initialLongTermValueByMedicationId[medication.Model.Id] ??= medication.Model.IsLongTerm;

                    medication.Model.Dosages?.forEach(dosage => {
                        s.initialDurationInDaysByDosageId[dosage.Id] ??= medication.TicksToDays(dosage.Duration);
                    });
                });
            }
        });
    }

    public componentDidMount(): void {
        $(window).trigger("resize");

        if (settingsStore.configuration.get("PharmacoTherapeuticActive") && this.props.PatientContactId && patientStore.getCurrentPatientContactId() === this.props.PatientContactId) {
            patientStore.PatientPrescriptionTabHandler.RefreshNumberOfAlertsVigilance(patientStore.getCurrentPatientContactId());
        }

        if (this.props.showPatientDetails && this.subHeaderRef?.current) {
            this.subHeaderResizeObserver.observe(this.subHeaderRef.current);
        }

        if (!IsNullOrUndefined(this.props.Model?.get("generalInstructions"))) {
            this.setState(p => ({ ...p, generalInstructions: this.props.Model.get("generalInstructions") }));
        }

        if (!IsNullOrUndefined(this.props.Model?.get("isHeaderExpanded"))) {
            this.setState(p => ({ ...p, isHeaderExpanded: this.props.Model.get("isHeaderExpanded") }));
        }
    }

    public componentDidUpdate(prevProps: Readonly<IPatientPrescriptionTabProps>): void {
        const prevPropsPrescriptionId: string = prevProps.Model?.get("prescriptionId");
        const currentPropsPrescriptionId: string = this.props.Model?.get("prescriptionId");

        if (!!currentPropsPrescriptionId && prevPropsPrescriptionId !== currentPropsPrescriptionId) {
            this.setState(p => ({
                ...p,
                generalInstructions: this.props.Model.get("generalInstructions"),
                isHeaderExpanded: this.props.Model.get("isHeaderExpanded")
            }));
        }
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        if (this.props.showPatientDetails)
            this.subHeaderResizeObserver.disconnect();
    }

    private renderRightSideBody(): JSX.Element {
        if (this.props.children)
            return this.renderCustomRightSide(this.props.Model);

        return this.rightSideBody(this.props.Model);
    }

    private rightSide(): JSX.Element {
        var result: JSX.Element = (
            <div className="tbc right-primary">
                {this.rightSideHeader()}
                {this.renderRightSideBody()}
            </div>
        );
        return result;
    }

    private rightSideHeader(): JSX.Element {
        const addFavoriteClassName: string = `pointer ${!!this.props.isAddFavoriteDisabled ? this.props.addFavoriteDisabledCssName : ""}`;
        const onAddFavoriteClick = !!this.props.isAddFavoriteDisabled ? null : this.props.toggleAddAsFavorite.bind(this);

        return (
            <div className="banner" style={{ "paddingTop": "3px" }}>
                <div className="flex-container" style={{ "justifyContent": "space-between" }}>
                    <div className="flex-container" style={{ "justifyContent": "flex-start" }}>
                        <SearchMedication
                            debounce={200}
                            onItemSelect={(item: MedicationSearchListItem) => this.props.onAddSelectedMedication(item)}
                            newValue={this.props.Model.get("SearchText")}
                            newValueEventId={this.props.Model.get("SearchTextEventId")}
                            encounterSiteId={this.props.encounterSiteId}
                            disableFavorites={!!this.props.isSearchedMedicationDisabled}
                            disableIndicator={!!this.props.isSearchedMedicationDisabled}
                            isInputDisabled={!!this.props.isSearchedMedicationDisabled}
                            dataCy="prescriptionTab-searchMedication"
                        />

                        <div className="editablecell" style={{ width: "210px", padding: "8px 0px 0px 0px" }}>
                            {/* <HyperLinkControl operation={null} text="Par Indication" marginLeft="10px" marginRight="10px" /> */}
                            <HyperLinkControl
                                onChange={() => this.props.onAddEmptyMedication()}
                                text={cacheStore.GetString("EmptyRxLabel")}
                                marginLeft="10px"
                                marginRight="10px"
                                className={this.props.emptyRxDisabledCssName || ""}
                                dataCy="prescriptionTab-emptyRxButton"
                            />
                            {/* <HyperLinkControl operation={null} text="★" marginLeft="10px" marginRight="10px" /> */}
                        </div>
                    </div>

                    <div className="flex-container" style={{ justifyContent: "flex-start", marginRight: "4px" }}>
                        {settingsStore.configuration.get("PharmacoTherapeuticActive") && (
                            <VigilanceIcon
                                id="_vigilanceLink"
                                styles={{ marginTop: "8px", marginRight: "12px" }}
                                height="22px"
                                onClick={() => VigilanceQueryViewerPopupHelper.openVigilancePopupForSearch()}
                            />
                        )}
                        {this.props.Model.get("IsFavorite") == true && (
                            <div className={addFavoriteClassName} onClick={onAddFavoriteClick} data-cy="prescriptionTab-favorite">
                                <img style={{ "marginTop": "8px", "height": "22px" }} id="_setFavOn" src={img_icon_favorite} />
                            </div>
                        )}
                        {this.props.Model.get("IsFavorite") == false && (
                            <div className={addFavoriteClassName} onClick={onAddFavoriteClick} data-cy="prescriptionTab-favorite">
                                <img style={{ "marginTop": "8px", "height": "22px" }} id="_setFavOff" src={img_icon_addfavorite} />
                            </div>
                        )}
                        {this.props.shouldHeaderContainGlobalRenewalNumber && this.renderGlobalRenewalNumber(this.props.Model)}
                    </div>
                </div>
            </div>
        );
    }

    private rightSideBody(model: IPrescriptionTabModel): JSX.Element {
        const metaFooterHeight: number = 70;    // this value should be equal to the height of the element having the class name, meta-footer

        return (
            <div>
                <div className="activemed-collection">
                    <div data-hvar={metaFooterHeight} className="scrollable" style={{ "overflowY": "scroll", "overflowX": "hidden" }}>
                        {!this.props.shouldHeaderContainGlobalRenewalNumber && this.renderGlobalRenewalNumber(model)}
                        {this.rightSideBodyMedicationModel(model)}
                    </div>
                </div>

                <div className={`meta-footer ${styles.tabFooter} ${!settingsStore.configuration.get("PharmacoTherapeuticActive") ? styles.removeVigilanceAlert : ""}`}>
                    {this.isResident && (
                        <div className={styles.supervisor}>
                            <label className={styles.supervisorLabelText}>{cacheStore.GetString("SupervisorLabel")}</label>
                            <div className={styles.supervisorSelect}>
                                {!!this.state.selectedSupervisorContactId && (
                                    <div className={`flex-container ${styles.supervisorName}`}>
                                        <div className="flex-display flex-align-items-center">
                                            <label className="margin-none-bottom">{this.state.selectedSupervisorName}</label>
                                            <span className={`glyphicon glyphicon-remove red-text pointer push-right ${styles.supervisorRemove}`} onClick={() => this.handleRemoveSelectedClinician()} />
                                        </div>
                                    </div>
                                )}
                                {IsNullOrWhiteSpace(this.state.selectedSupervisorContactId) && (
                                    <div>
                                        <TextboxControl
                                            autoCompleteItems={this.supervisors.map(x => new SearchableItemModelT<Person>(x.NameWithPrefix, x.ContactId, x)).toImmutable()}
                                            onAutoCompleteSelectCallBack={(code, model) => this.handleSupervisorSelected(model)}
                                            onAutoCompleteSelectClearTextBox={true}
                                            value={this.state.selectedSupervisorName}
                                            reversePopup
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {!this.isResident && (
                        <div />
                    )}

                    {this.renderVigilanceAlerts(model)}

                    <div className={styles.footerButtons}>
                        {model.get("IsLoading") && (
                            <HyperLinkControl
                                operation={LocalPatientOperations.PrescriptionTab.CancelPrescribeAction}
                                text="Cancel"
                                marginLeft="10px"
                                marginRight="10px"
                            />
                        )}
                        <button
                            className="btn btn-primary"
                            data-cy="patientPrescriptionTab-prescribe"
                            onClick={() => this.Prescribe()}
                            disabled={(model.get("IsLoading") || this.state.IsEmptyPrescription)}
                        >
                            {cacheStore.GetString("PrescribeLabel")}
                        </button>

                        {cacheStore.ClinicSettings.EnableSignedAuthorization && (
                            <button
                                className="btn btn-primary"
                                data-cy="patientPrescriptionTab-sign"
                                onClick={() => this.Prescribe({ signCopy: true })}
                                disabled={(model.get("IsLoading") || this.state.IsEmptyPrescription)}
                            >
                                {cacheStore.GetString("SignLabel")}
                            </button>
                        )}

                        <button
                            className="btn btn-primary"
                            onClick={() => this.Prescribe({ printCopy: true })}
                            disabled={(model.get("IsLoading") || this.state.IsEmptyPrescription)}
                        >
                            {cacheStore.GetString("PrintLabel")}
                        </button>

                        {sessionStore.faxAccess && (sessionStore.signOnPrintAccess || cacheStore.ClinicSettings.EnableQuickDocumentSigning) && (
                            <button
                                className="btn btn-primary"
                                onClick={() => this.Prescribe({ sendFax: true })}
                                disabled={(model.get("IsLoading") || this.state.IsEmptyPrescription)}
                            >
                                {cacheStore.GetString("FaxVerbLabel")}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    private rightSideBodyMedicationModel(model: IPrescriptionTabModel): JSX.Element {
        var self = this;

        var routes: Immutable.List<ComboBoxDropDownItem> = Immutable.List(cacheStore.RoutesCache.map(x => Route.ToComboboxItem(x)));
        var dosageTypes: Immutable.List<FlatLookupView> = Immutable.List(cacheStore.FindLookupsByTypeCode("DosageType").map(x => x));
        var frequencies: Immutable.List<ComboBoxDropDownItem> = Immutable.List(cacheStore.FrequenciesCache.map(x => Frequency.ToComboboxItem(x)));
        var dosageUnits: Immutable.List<ComboBoxDropDownItem> = Immutable.List(cacheStore.UnitsCache.map(x => Unit.toComboboxItem(x)));

        let sortableMedicationRenderFunction: ISortableItemRenderFunction<MedicationModel> =
            function(medication: MedicationModel, idx: number): JSX.Element {
                return self.RenderMedicationModel(medication, idx, routes, dosageTypes, frequencies, dosageUnits);
            };

        var MedicationItemUIModels = Immutable.List(model
            .get("MedicationModels")
            .map(x => {
                var sortable = new SortableItemUIModel<MedicationModel>();
                sortable.ItemId = x.Model.Id;
                sortable.ItemIndex = x.Model.EMRTemplateSortIndex;
                sortable.Model = x;
                return sortable;
            })
            .toList()
        );

        var sortableMedicationContainer = (
            <SortableContainerForActiveMedication
                ComponentId="medicationContainer"
                DragGroupClass="draggroup-prescriptionmed"
                isMedicationModel
                ItemRenderingFunction={sortableMedicationRenderFunction}
                SortableItems={MedicationItemUIModels}
                OnSortDoneOperation={LocalPatientOperations.PrescriptionTab.ReorderMedicationDragAndDrop}
                DragStartDelegate={this.dragStartForSingleDropZone}
                DragStopDelegate={this.dragStopForSingleDropZone}
            />
        );

        return sortableMedicationContainer;
    }

    private dragStartForSingleDropZone(event: JQueryEventObject, ui: JQueryUI.SortableUIParams): void {
        ui.item.closest(".drag-container").addClass("over");
    }

    private dragStopForSingleDropZone(event: JQueryEventObject, ui: JQueryUI.SortableUIParams): void {
        ui.item.closest(".drag-container").removeClass("over");
    }

    private RenderMedicationModel(medicationModel: MedicationModel, idx: number, routes: Immutable.List<ComboBoxDropDownItem>, dosageTypes: Immutable.List<FlatLookupView>, frequencies: Immutable.List<ComboBoxDropDownItem>, dosageUnits: Immutable.List<ComboBoxDropDownItem>): JSX.Element {
        const estimateEndDate = medicationModel.EffectiveEndDate() != null ? medicationModel.EffectiveEndDate() : DateTime.nowDeprecated();
        const totalDurationNumberOfDays: number = medicationModel.TicksToDays(medicationModel.Model.TotalDurationManual);
        const lastDosage = medicationModel.GetLastDosage();
        const dosageCount = medicationModel.Model.Dosages?.count();
        const numberOfRefill = medicationModel.Model.NumberOfRefill == null ? "" : medicationModel.Model.NumberOfRefill.toString();
        const requiresTotalDuration = true;// (medicationModel.Model.Dosages == null) || !medicationModel.Model.Dosages.every((x) => x.DisplayMode != MedicationDosageDisplayMode.Quantity) || medicationModel.Model.Dosages.count() > 1;
        const isNarcotic = medicationModel.IsNarcotic();
        const shouldShowFirstDispensationQuantity = MedicationModel.shouldShowFirstDispensationQuantity(isNarcotic, dosageCount);
        const shouldShowRefillQuantity = MedicationModel.shouldShowRefillQuantity(isNarcotic, dosageCount, lastDosage.DisplayMode);
        const shouldShowSingleDosageQuantityInsteadOfDuration = MedicationModel.shouldShowSingleDosageQuantityInsteadOfDuration(dosageCount, lastDosage.DisplayMode);
        const isInterrupted = medicationModel.isInterrupted();
        const shouldShowOriginalMedicationReason = medicationModel.shouldShowOriginalMedicationReason();

        let medicationModelDosages: JSX.Element[] = [];
        if (medicationModel.Model.Dosages != null && medicationModel.Model.Dosages.count() != 0) {
            medicationModel.Model.Dosages = medicationModel.Model.Dosages.sort((cur: MedicalItemMedicationAndPrescriptionDosage, next: MedicalItemMedicationAndPrescriptionDosage) => {
                if (cur.RankPosition == next.RankPosition)
                    return 0;
                else
                    return cur.RankPosition > next.RankPosition ? 1 : -1;
            }).toList();

            // Render Dosages For Edit Mode
            if (medicationModel.IsInEditMode)
                medicationModelDosages = this.RenderDosagesForEditMode(medicationModel, dosageTypes, frequencies, dosageUnits);

            // Render Dosages For Preview Mode
            if (!medicationModel.IsInEditMode)
                medicationModelDosages = this.RenderDosagesForPreviewMode(medicationModel, idx, shouldShowFirstDispensationQuantity, shouldShowRefillQuantity);
        }

        // Render Medication
        return (
            <div key={"__a" + idx}>
                {medicationModel.IsInEditMode && (
                    <MedicationEditForm
                        medicationModel={medicationModel}
                        routes={routes}
                        totalDurationNumberOfDays={totalDurationNumberOfDays}
                        estimateEndDate={estimateEndDate}
                        numberOfRefill={numberOfRefill}
                        requiresTotalDuration={requiresTotalDuration}
                        currentIdx={idx}
                        isPreviewButtonVisible
                        isDeletable
                        fixTextSelectionInterferingWithCheckbox
                        onMedicationNameChange={newText => {
                            sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.TextBoxChange, {
                                value: newText,
                                arg1: medicationModel.Model.Id,
                                arg2: "",
                                arg3: PrescriptionMedicationTextBoxes.MedicationName
                            });
                        }}
                        onLongTermChange={isChecked => this.onLongTermChange(isChecked, medicationModel, true)}
                        onDoNotSubstituteChange={(isChecked: boolean): void => {
                            sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.CheckBoxChange, {
                                checked: isChecked,
                                arg1: medicationModel.Model.Id,
                                arg2: "",
                                arg3: PrescriptionMedicationCheckBoxes.DoNotSubstitute
                            });
                        }}
                        onRouteChange={selectedCode => {
                            sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.ComboBoxChange, {
                                value: selectedCode,
                                arg1: medicationModel.Model.Id,
                                arg2: "",
                                arg3: PrescriptionMedicationKendoComboBoxType.Route
                            });
                        }}
                        onInstructionChange={newText => {
                            sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.TextBoxChange, {
                                value: newText,
                                arg1: medicationModel.Model.Id,
                                arg2: "",
                                arg3: PrescriptionMedicationTextBoxes.Instruction
                            });
                        }}
                        onInternalNoteChange={newText => {
                            sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.TextBoxChange, {
                                value: newText,
                                arg1: medicationModel.Model.Id,
                                arg2: "",
                                arg3: PrescriptionMedicationTextBoxes.MedicationNote
                            });
                        }}
                        onAddDosageClick={(): void => {
                            sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.AddDosage, {
                                id: medicationModel.Model.Id
                            });
                        }}
                        onInitialRefillQuantityChange={newText => {
                            sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.TextBoxChange, {
                                value: newText,
                                arg1: medicationModel.Model.Id,
                                arg2: "",
                                arg3: PrescriptionMedicationTextBoxes.InitialRefillQuantity
                            });
                        }}
                        onQuantityOrDurationChange={newText => {
                            sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.TextBoxChange, {
                                value: newText,
                                arg1: medicationModel.Model.Id,
                                arg2: medicationModel.Model.Dosages.first().Id,
                                arg3: shouldShowSingleDosageQuantityInsteadOfDuration ?
                                    PrescriptionMedicationTextBoxes.DosageQuantity_BottomField : PrescriptionMedicationTextBoxes.DosageDuration_BottomField
                            });
                        }}
                        onRenewalChange={newText => {
                            sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.TextBoxChange, {
                                value: newText,
                                arg1: medicationModel.Model.Id,
                                arg2: "",
                                arg3: PrescriptionMedicationTextBoxes.Renewal
                            });
                        }}
                        onRefillQuantityChange={newText => {
                            sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.TextBoxChange, {
                                value: newText,
                                arg1: medicationModel.Model.Id,
                                arg2: "",
                                arg3: PrescriptionMedicationTextBoxes.RefillQuantity
                            });
                        }}
                        onTotalDurationChange={newText => {
                            sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.TextBoxChange, {
                                value: newText,
                                arg1: medicationModel.Model.Id,
                                arg2: "",
                                arg3: PrescriptionMedicationTextBoxes.TotalDuration
                            });
                        }}
                        onStartDateChange={(startDate, startDatePrecision) => {
                            sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.StartDateChange, {
                                startDate: startDate,
                                startDatePrecision: startDatePrecision,
                                medicationId: medicationModel.Model.Id
                            });
                        }}
                        onToggleEditPreviewMedicationModelClick={(): void => {
                            sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.ToggleEditPreviewMedicationModel, {
                                id: medicationModel.Model.Id
                            });
                        }}
                        onRemoveMedicationClick={(): void => {
                            if (this.props.onRemoveMedication) {
                                this.props.onRemoveMedication(medicationModel);
                            }
                            else {
                                this.RemoveMedication(medicationModel.Model.Id);
                            }
                        }}
                    >
                        {medicationModelDosages}
                    </MedicationEditForm>
                )}

                {!medicationModel.IsInEditMode && (medicationModel.Model.PrescriptionTab_PendingStatus == null) && (
                    <div
                        className="activemeditem"
                        onClick={() => {
                            var operation = new LocalEvent(LocalPatientOperations.PrescriptionTab.ToggleEditPreviewMedicationModel);
                            operation.AddItem("id", medicationModel.Model.Id);
                            LocalEventDispatcher.dispatch(operation);
                        }}
                    >
                        <div className="flex-container"> {/* Row 1 */}
                            <div className="flex-container">
                                <div className="editablecell Column1" style={{ width: "220px" }}>
                                    <div
                                        className={styles.medicationNameContainer}
                                        data-cy="prescriptionTab-previewMedicationNameContainer"
                                    >
                                        {cacheStore.ClinicSettings.EnableLongTermMedication && medicationModel.Model.IsLongTerm && (
                                            <LongTermMedicationIcon dataCy="prescriptionTab-longTermMedicationIcon" />
                                        )}
                                        <span id={medicationModel.Model.Id} style={{ color: "#0070d2", "fontWeight": "bold" }}>{medicationModel.GetName()}</span>
                                    </div>
                                    {!medicationModel.Model.IsSubstituable &&
                                        <div><span>{cacheStore.GetString("NotSubstituableLabel")}</span></div>
                                    }
                                </div>
                                <div className="editablecell Column2">
                                    {medicationModelDosages}
                                </div>
                            </div>
                            <div className="right-side text-right">
                                <div className="flex-container">
                                    {medicationModel.IsLoading &&
                                        <div className="editablecell" style={{ margin: "0px 10px 0px 0px" }}><span> Loading... </span></div>
                                    }
                                    <div
                                        className="editablecell"
                                        onClick={this.props.onRemoveMedication ?
                                            () => this.props.onRemoveMedication(medicationModel) :
                                            () => this.RemoveMedication(medicationModel.Model.Id)
                                        }
                                    >
                                        <img
                                            style={{ "width": "16px", "marginTop": "-5px" }}
                                            alt="deleteMed_2"
                                            className="file-img"
                                            src={img_icon_delete}
                                            aria-expanded="false"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex-container meds" style={{ "marginBottom": "6px", "flexDirection": "column", "alignItems": "flex-start" }}> {/* Row 2 */}
                            {medicationModel.PrescriptionTab_OriginalMedication != null && (
                                <div className="editablecell" style={{ width: "534px", "marginLeft": "20px" }}>
                                    <div className="flex-container meds" style={{ "alignItems": "flex-start", "whiteSpace": "nowrap" }}>
                                        <IncreasedOrDecreasedMedicationText
                                            medicationModel={medicationModel}
                                            isDosageStringVisible
                                        />
                                    </div>
                                </div>
                            )}
                            {shouldShowOriginalMedicationReason && (
                                <div className="editablecell" style={{ width: "534px", "marginLeft": "40px" }}>
                                    <div className="flex-container meds" style={{ "alignItems": "flex-start", "whiteSpace": "nowrap" }}>
                                        <div style={{ "width": "281px", "marginLeft": "12px", "textOverflow": "ellipsis", "fontStyle": "italic", "overflow": "hidden" }}><span>{cacheStore.GetString("ReasonLabel") + ": " + medicationModel.getOriginalMedicationReason()}</span></div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex-container"> {/* Row 3 */}
                            <div className="flex-container">
                                <div style={{ width: "450px", fontStyle: "italic" }}>{JSXHelper.BreakStringIntoParagraphs(medicationModel.Model.Instruction)}</div>
                            </div>
                        </div>
                    </div>
                )}

                {!medicationModel.IsInEditMode && !(medicationModel.Model.PrescriptionTab_PendingStatus == null) && (
                    <div className="activemeditem">
                        <div className="flex-container"> {/* Row 1 */}
                            <div className="flex-container">
                                <div className="editablecell">
                                    <div><SpanControl id={"status_" + medicationModel.Model.Id} style={{ color: "red", "fontWeight": "bold" }} text={medicationModel.Model.PrescriptionTab_PendingStatus == null ? "" : MedicationModel.PrescriptionStatusIntToValue(Number(medicationModel.Model.PrescriptionTab_PendingStatus)) + " -"} operation={LocalPatientOperations.PrescriptionTab.ToggleEditPreviewMedicationModel} /></div>
                                </div>
                                <div className="editablecell" style={{ width: "285px", "marginLeft": "5px" }} data-cy="prescriptionTab-previewMedicationNameContainer">
                                    <div><SpanControl id={"name_" + medicationModel.Model.Id} style={{ color: "red", "fontWeight": "bold" }} text={medicationModel.GetName()} operation={LocalPatientOperations.PrescriptionTab.ToggleEditPreviewMedicationModel} /></div>
                                </div>
                                {isInterrupted && (
                                    <div className="editablecell">
                                        <div><SpanControl id={"date_" + medicationModel.Model.Id} style={{ "fontStyle": "italic" }} text={medicationModel.getLabeledCeaseDate()} operation={LocalPatientOperations.PrescriptionTab.ToggleEditPreviewMedicationModel} /></div>
                                    </div>
                                )}
                            </div>
                            <div className="right-side text-right">
                                <div
                                    className="editablecell"
                                    onClick={this.props.onRemoveMedication ?
                                        () => this.props.onRemoveMedication(medicationModel) :
                                        () => this.RemoveMedication(medicationModel.Model.Id)
                                    }
                                >
                                    <img
                                        style={{ "width": "16px", "marginTop": "-5px" }}
                                        alt="deleteMed_3"
                                        className="file-img"
                                        src={img_icon_delete}
                                        aria-expanded="false"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex-container"> {/* Row 2 */}
                            <div className="editablecell">
                                <div><span style={{ "width": "281px", "marginLeft": "12px", "textOverflow": "ellipsis", "fontStyle": "italic", "overflow": "hidden" }}>{cacheStore.GetString("ReasonLabel") + ": "}<SpanControl id={"reason_" + medicationModel.Model.Id} text={medicationModel.getReason()} operation={LocalPatientOperations.PrescriptionTab.ToggleEditPreviewMedicationModel} /></span></div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private RenderDosagesForEditMode(medicationModel: MedicationModel, dosageTypes: Immutable.List<FlatLookupView>, frequencies: Immutable.List<ComboBoxDropDownItem>, dosageUnits: Immutable.List<ComboBoxDropDownItem>): JSX.Element[] {
        const medicationModelDosages: JSX.Element[] = [];

        const IsNarcotic = medicationModel.IsNarcotic();

        medicationModel.Model.Dosages.forEach((currentDosage: MedicalItemMedicationAndPrescriptionDosage, idy: number) => {
            const numberofDays: number = medicationModel.TicksToDays(currentDosage.Duration);

            // Fix Default Values when null
            const selectedDosageTypeId = MedicalItemMedicationAndPrescriptionDosage.getSelectedDosageTypeId(currentDosage.DosageTypeLookupId);
            const selectedDosageUnitId = currentDosage.DosageUnit?.toString();

            medicationModelDosages.push(
                <DosageEditForm
                    key={currentDosage.Id}
                    medicationModelModelId={medicationModel.Model.Id}
                    currentDosageId={currentDosage.Id}
                    currentDosageString={currentDosage.DosageString}
                    currentDosageFrequency={currentDosage.DosageFrequency}
                    currentDosageIfNeeded={currentDosage.IfNeeded}
                    currentDosageServedFrequency={currentDosage.DosageServedFrequency}
                    currentDosageServedDays={currentDosage.DosageServedDays}
                    isNarcotic={medicationModel.IsNarcotic()}
                    numberOfDays={medicationModel.TicksToDays(currentDosage.Duration)}
                    isFirstDosage={idy === 0}
                    isLastDosage={idy === (medicationModel.Model.Dosages.count() - 1)}
                    isFakeDosage={currentDosage.IsFakeDosage}
                    selectedDosageTypeId={selectedDosageTypeId}
                    selectedDosageUnitId={selectedDosageUnitId}
                    dosageTypes={dosageTypes}
                    frequencies={frequencies}
                    dosageUnits={dosageUnits}
                    onDosageStringChange={(newText: string): void => {
                        sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.TextBoxChange, {
                            value: newText,
                            arg1: medicationModel.Model.Id,
                            arg2: currentDosage.Id,
                            arg3: PrescriptionMedicationTextBoxes.DosageString
                        });
                    }}
                    onDosageUnitChange={(selectedCode: string): void => {
                        sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.ComboBoxChange, {
                            value: selectedCode,
                            arg1: medicationModel.Model.Id,
                            arg2: currentDosage.Id,
                            arg3: PrescriptionMedicationKendoComboBoxType.DosageUnit
                        });
                    }}
                    onDosageTypeChange={(selectedLookupId: string): void => {
                        sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.ComboBoxChange, {
                            value: selectedLookupId,
                            arg1: medicationModel.Model.Id,
                            arg2: currentDosage.Id,
                            arg3: PrescriptionMedicationKendoComboBoxType.DosageType
                        });
                    }}
                    onDosageFrequencyChange={(selectedCode: string): void => {
                        sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.ComboBoxChange, {
                            value: selectedCode,
                            arg1: medicationModel.Model.Id,
                            arg2: currentDosage.Id,
                            arg3: PrescriptionMedicationKendoComboBoxType.DosageFrequency
                        });
                    }}
                    onDosageIfNeededChange={(isChecked: boolean): void => {
                        sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.CheckBoxChange, {
                            checked: isChecked,
                            arg1: medicationModel.Model.Id,
                            arg2: currentDosage.Id,
                            arg3: PrescriptionMedicationCheckBoxes.DosageIfNeeded
                        });
                    }}
                    onDosageServedFrequencyChange={(newText: string): void => {
                        sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.TextBoxChange, {
                            value: newText,
                            arg1: medicationModel.Model.Id,
                            arg2: currentDosage.Id,
                            arg3: PrescriptionMedicationTextBoxes.DosageServedFrequency
                        });
                    }}
                    onDosageServedDaysChange={(newText: string): void => {
                        sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.TextBoxChange, {
                            value: newText,
                            arg1: medicationModel.Model.Id,
                            arg2: currentDosage.Id,
                            arg3: PrescriptionMedicationTextBoxes.DosageServedDays
                        });
                    }}
                    onDeleteDosageClick={(): void => {
                        this.onDeleteDosage(medicationModel.Model.Id, currentDosage.Id);
                    }}
                >
                    {this.RenderDosageDurationOrQuantity(currentDosage, numberofDays, medicationModel, IsNarcotic)}
                </DosageEditForm>
            );
        });

        return medicationModelDosages;
    }

    private RenderDosagesForPreviewMode(medicationModel: MedicationModel, idx: number, shouldShowFirstDispensationQuantity: boolean, shouldShowRefillQuantity: boolean): JSX.Element[] {
        var medicationModelDosages: JSX.Element[] = [];
        var IsNarcotic = medicationModel.IsNarcotic();

        const isSingleDose = medicationModel.Model.Dosages.count() == 1;
        medicationModel.Model.Dosages.forEach((currentDosage: MedicalItemMedicationAndPrescriptionDosage, idy: number) => {
            var numberofDays: number = medicationModel.TicksToDays(currentDosage.Duration);

            var isLastDosage = idy == medicationModel.Model.Dosages.count() - 1;
            var IsFirstDosage = idy == 0;

            var FreqCodeReduced = medicationModel.GetFrequencyValueReduced(currentDosage.Id);
            var UnitValue = medicationModel.getDosageUnitValue(currentDosage.Id);

            let ServedQuantityClean = currentDosage.DosageServedQuantity ?? "";

            const servedQuantityString = `#${ServedQuantityClean}${!isNaN(ServedQuantityClean as any) ? " " + UnitValue : ""}`;
            const durationString = numberofDays.toString() + cacheStore.GetString("DaysLabel").substr(0, 1).toLowerCase();
            const firstDispensationString = medicationModel.getFirstDispensationString();
            medicationModelDosages.push(
                <div key={`dosage-${currentDosage.Id}`}>
                    <div className="flex-container flex-start">
                        {!IsFirstDosage && (
                            <div style={{ "width": "0px" }}>
                                <div style={{ "marginLeft": "-80px" }}>
                                    <span style={{ "fontWeight": "bold" }}>{cacheStore.GetString("FollowedByLabel") + ": "}</span>
                                </div>
                            </div>
                        )}
                        <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                            <div className="editablecell nowrapcell" style={{ "minWidth": "17px" }}><span>{currentDosage.DosageString}</span></div>
                            <div className="editablecell nowrapcell" style={{ "minWidth": "55px" }}><span>{UnitValue}</span></div>
                            <div className="editablecell nowrapcell" style={{ "minWidth": "30px" }}><span>{medicationModel.Model.Route}</span></div>
                            <div className="editablecell nowrapcell" style={{ "minWidth": "60px" }}><span>{FreqCodeReduced}</span></div>
                        </div>

                        <div className="editablecell nowrapcell" style={{ "width": "32px", "fontWeight": "bold" }}>
                            <span>{currentDosage.IfNeeded ? "PRN" : ""}</span>
                        </div>

                        <div style={{ display: "flex", columnGap: "5px" }}>
                            {!isSingleDose && currentDosage.DisplayMode == MedicationDosageDisplayMode.Quantity && (
                                <div>{`${servedQuantityString}`}</div>
                            )}
                            {!isSingleDose && currentDosage.DisplayMode == MedicationDosageDisplayMode.Duration && (
                                <div>{durationString}</div>
                            )}
                            {isSingleDose && !!numberofDays && (
                                <div>{durationString}</div>
                            )}
                            {isSingleDose && !IsNullOrWhiteSpace(ServedQuantityClean) && !IsNarcotic && (!!numberofDays) && (
                                <div>{`(${servedQuantityString})`}</div>
                            )}
                            {isSingleDose && !IsNullOrWhiteSpace(ServedQuantityClean) && (IsNarcotic || !numberofDays) && (
                                <div>{`${servedQuantityString}`}</div>
                            )}
                            {isLastDosage && currentDosage.DisplayMode != MedicationDosageDisplayMode.Quantity && shouldShowRefillQuantity && !IsNullOrWhiteSpace(medicationModel.Model.RefillQuantity) && (
                                <div>(#{medicationModel.Model.RefillQuantity})</div>
                            )}
                        </div>
                        {isLastDosage && !IsNarcotic && !!medicationModel.Model.NumberOfRefill && (
                            <div className="editablecell" style={{ width: "40px", marginLeft: "3px" }}>
                                <span>R{medicationModel.Model.NumberOfRefill}</span>
                            </div>
                        )}
                        {isLastDosage && IsNarcotic && (
                            <div className="editablecell" style={{ width: "40px", marginLeft: "3px", fontWeight: "bold" }}>
                                <span>NR</span>
                            </div>
                        )}
                    </div>
                    {isLastDosage && shouldShowFirstDispensationQuantity && !IsNullOrWhiteSpace(firstDispensationString) && (
                        <div className="bold">{firstDispensationString}</div>
                    )}
                </div>
            );

            const servedFreq = PatientMedicalItemsHelper.GetMedicationNarcoticFrequency(currentDosage);
            const servedQty = PatientMedicalItemsHelper.GetMedicationNarcoticQuantity(currentDosage, UnitValue);

            if (IsNarcotic) {
                const narcoticSeparator = servedFreq && servedQty ? ", " : "";
                medicationModelDosages.push(
                    <div className="flex-container" style={{ justifyContent: "flex-start" }} key={"dosageNarco_" + idx + "_" + idy}>
                        <div className="flex-container" style={{ justifyContent: "flex-start", "minWidth": "135px", "marginLeft": "23px" }}>
                            <div className="editablecell nowrapcell" style={{ "fontStyle": "italic", "paddingRight": "2px" }}>
                                <span>{servedFreq}{narcoticSeparator}{servedQty}</span>
                            </div>
                        </div>
                    </div>
                );
            }
        });

        return medicationModelDosages;
    }

    private RenderDosageDurationOrQuantity(currentDosage: MedicalItemMedicationAndPrescriptionDosage, numberofDays: number, medicationModel: MedicationModel, IsNarcotic: boolean): JSX.Element {
        const medicalItemId = medicationModel.Model.Id;

        const onDurationOrQuantityClick = (mode: MedicationDosageDisplayMode, unit: MedicationDosageDisplayModeDurationUnit): void => {
            sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.SetMedicationModelDurationMode, {
                id: medicalItemId,
                target: currentDosage.Id,
                mode,
                unit
            });
        };

        const onDurationOrQuantityChange = (newText: string, targetField: PrescriptionMedicationTextBoxes, unit: MedicationDosageDisplayModeDurationUnit): void => {
            sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.TextBoxChange, {
                value: newText,
                arg1: medicalItemId,
                arg2: currentDosage.Id,
                arg3: targetField,
                unit: unit
            });
        };

        return (
            <DosageDurationOrQuantityEditForm
                medicalItemId={medicalItemId}
                currentDosageId={currentDosage.Id}
                currentDosageDisplayMode={currentDosage.DisplayMode}
                currentDosageDisplayModeDurationUnit={currentDosage.DisplayModeDurationUnit}
                currentDosageNarcoticServedQuantity={currentDosage.DosageServedQuantity ?? ""}
                numberOfDays={numberofDays}
                isNarcotic={IsNarcotic}
                onDosageDurationDaysClick={(): void => {
                    onDurationOrQuantityClick(MedicationDosageDisplayMode.Duration, MedicationDosageDisplayModeDurationUnit.Days);
                }}
                onDosageDurationWeeksClick={(): void => {
                    onDurationOrQuantityClick(MedicationDosageDisplayMode.Duration, MedicationDosageDisplayModeDurationUnit.Weeks);
                }}
                onDosageDurationMonthsClick={(): void => {
                    onDurationOrQuantityClick(MedicationDosageDisplayMode.Duration, MedicationDosageDisplayModeDurationUnit.Months);
                }}
                onDosageQuantityClick={(): void => {
                    onDurationOrQuantityClick(MedicationDosageDisplayMode.Quantity, null);
                }}
                onDosageDurationChange={(newText: string): void => {
                    onDurationOrQuantityChange(newText, PrescriptionMedicationTextBoxes.DosageDuration, currentDosage.DisplayModeDurationUnit);
                    this.mutateState(
                        s => s.isDurationManuallyChangedByDosageId[currentDosage.Id] = true,
                        () => {
                            if (
                                this.state.isLongTermManuallyChangedByMedicationId[medicalItemId]
                                || !settingsStore.configuration.get("LongTermMedicationDurationInDays")
                                || medicationModel.Model.IsShortTerm
                            )
                                return;

                            // Automatically update long term checkbox
                            const durationInDays = MedicalItemMedicationAndPrescriptionDosage.getDurationValueInDays(newText, currentDosage.DisplayModeDurationUnit);

                            this.onLongTermChange(
                                durationInDays === settingsStore.configuration.get("LongTermMedicationDurationInDays") || this.state.initialLongTermValueByMedicationId[medicalItemId],
                                medicationModel,
                                false
                            );
                        }
                    );
                }}
                onDosageQuantityChange={(newText: string): void => {
                    onDurationOrQuantityChange(newText, PrescriptionMedicationTextBoxes.DosageQuantity, currentDosage.DisplayModeDurationUnit);
                }}
            />
        );
    }

    private onDeleteDosage(medicationId: string, dosageId: string): void {
        var operation = new LocalEvent(LocalPatientOperations.PrescriptionTab.DeleteDosage);
        operation.AddItem("id", medicationId);
        operation.AddItem("dosageid", dosageId);
        LocalEventDispatcher.dispatch(operation);
    }

    private RemoveMedication(medicationId: string): void {
        var operation = new LocalEvent(LocalPatientOperations.PrescriptionTab.RemoveMedication);
        operation.AddItem("id", medicationId);
        LocalEventDispatcher.dispatch(operation);
    }

    private async Prescribe({ sendFax, printCopy, signCopy }: { sendFax?: boolean, printCopy?: boolean, signCopy?: boolean } = {}): Promise<void> {
        let encounterId = "";
        if (patientStore.IsMedicalItemFromOutsideNote) {
            const prescriptionLookupId = cacheStore.FindLookupByLookupCode("Prescription", "CONSULTATIONCONTEXT").LookupID;
            encounterId = await PatientMedicalItemsHelper.setOutsideNoteEncounter(null, prescriptionLookupId);
        }

        sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.Prescribe, {
            EncounterId: encounterId,
            sendFax: sendFax ?? false,
            printCopy: printCopy ?? false,
            signCopy: signCopy ?? false,
            withSignature: false
        });

        const renewalsTab = cacheStore.Tabs.find(p => p.key === MyleTabKey.PrescriptionRenewals);
        if (!renewalsTab)
            return;

        cacheStore.UpdateTabsPath(MyleTabKey.PrescriptionRenewals, renewalsTab.path.replace(/\/.+/, ""));
    }

    public toggleSelectedMedication(medicationListType: MedicationListType): void {
        if (this.props.onToggleSelectedMedicationList) {
            this.props.onToggleSelectedMedicationList(medicationListType);
        }
        else {
            const self = this;

            if (medicationListType == MedicationListType.DSQ && self.props.Model.get("DSQMedicationModels") == null) {
                if (this.IsDSQLoading == true)
                    return;

                var requestData = {
                    PatientContactId: this.props.PatientContactId,
                    StartDate: null,
                    EndDate: null,
                    ActiveMedicationOnly: true
                };

                // Loading DSQ
                sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.DSQMedsLoading, { isLoading: true });

                Client.PostDSQ(
                    ServerOperations.Server.DSQ_MedicationSummary,
                    requestData,
                    response => sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.DSQMedsLoaded, { Success: response.Errors.length == 0, Meds: response.myleDSQMedications }),
                    () => sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.DSQMedsLoaded, { Success: false })
                );
            }
            else {
                sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.ToggleSelectedMedicationList, { selectedMedicationListType: medicationListType });
            }
        }
    }

    private renderGlobalRenewalNumber(model: IPrescriptionTabModel): JSX.Element {
        const prescriptions = model.get("MedicationModels")?.toArray();
        if (prescriptions?.any()) {
            return (
                <div id="medicationContainerHeader" style={{ width: "100%", height: "35px", display: "flex", justifyContent: "flex-end", padding: "5px" }}>
                    <span style={{ marginTop: "5px", paddingRight: "5px", fontWeight: "bold" }}>
                        {cacheStore.GetString("UpdateAllRenewalNumber")}
                    </span>
                    <TextboxControl
                        IsSmall
                        IsNumeric
                        className="form-control input"
                        value={model.get("updateAllRenewalsNumber")?.toString() ?? ""}
                        style={{ width: "45px" }}
                        onChange={(newText: string) => this.props.updateGlobalRenewalsNumberChange(newText)}
                        disabled={this.props.isGlobalRenewalNumberDisabled}
                    />
                </div>
            );
        }
        return null;
    }

    private handleSupervisorSelected(model: Person): void {
        this.setState({ ...this.state, selectedSupervisorContactId: model.ContactId, selectedSupervisorName: cacheStore.GetCliniciansPersonsByContactId(model.ContactId).NameWithPrefix });
        sessionStore.lockedSupervisorContactId = model.ContactId;
    }

    private handleRemoveSelectedClinician(): void {
        this.setState({
            ...this.state,
            selectedSupervisorContactId: null,
            selectedSupervisorName: "",
            isSupervisorLocked: false
        });
        sessionStore.lockedSupervisorContactId = "";
    }

    private renderCustomRightSide(model: IPrescriptionTabModel): JSX.Element {
        return (
            <>
                {this.props.children}
                {this.renderVigilanceAlerts(model)}
            </>
        );
    }

    private renderVigilanceAlerts(model: IPrescriptionTabModel): JSX.Element {
        if (!settingsStore.configuration.get("PharmacoTherapeuticActive"))
            return null;

        const date = cacheStore.ClinicSettings.EnableDrugInteractionAlertManagement
            ? DateHelper.formatDateLuxon(model.get("VigilanceLastDatabaseUpdateDate"), "yyyy-MM-dd")
            : null;

        return (
            <div className={styles.vigilanceAlertFooter}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        {(model.get("VigilanceNumberOfAlerts") != null)
                            ? (
                                <VigilanceSanteAlert
                                    alertLevel={model.get("VigilanceSevereAlertLevel")}
                                    alertCount={model.get("VigilanceNumberOfAlerts")}
                                    onClick={this.props.onVigilanceSanteIconClick}
                                />
                            )
                            : <SpanControl IsBold text={cacheStore.GetString("LoadingLabel")} />
                        }
                    </div>
                    {date && (
                        <div className={styles.databaseUpdateDate}>
                            {`${cacheStore.GetString("DrugDatabaseUpdatedLabel")} `}
                            <span>{date}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    private openSecondScreen(): void {
        SecondScreenHelper.openWindow({ patientId: this.props.MedicalSummaryModel?.get("PatientContactId") });
    }

    private saveGeneralInstructions(): void {
        sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.SaveGeneralInstructions, { generalInstructions: this.state.generalInstructions });
    }

    private saveIsHeaderExpanded(headerState: boolean): void {
        sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.SaveIsHeaderExpanded, { isHeaderExpanded: headerState });
    }

    private onLongTermChange(isChecked: boolean, medicationModel: MedicationModel, isManualChange: boolean): void {
        if (!cacheStore.ClinicSettings.EnableLongTermMedication)
            return;

        sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.CheckBoxChange, {
            checked: isChecked,
            arg1: medicationModel.Model.Id,
            arg2: "",
            arg3: PrescriptionMedicationCheckBoxes.LongTerm
        });

        if (!isManualChange)
            return;

        this.mutateState(s => {
            s.isLongTermManuallyChangedByMedicationId[medicationModel.Model.Id] = true;

            if (
                !settingsStore.configuration.get("LongTermMedicationDurationInDays")
                || medicationModel.IsNarcotic()
                || medicationModel.Model.IsShortTerm
            )
                return;

            // Automatically update duration for all dosages
            medicationModel.Model.Dosages.forEach(dosage => {
                if (s.isDurationManuallyChangedByDosageId[dosage.Id] || dosage.DisplayMode === MedicationDosageDisplayMode.Quantity)
                    return;

                sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.TextBoxChange, {
                    value: isChecked
                        ? settingsStore.configuration.get("LongTermMedicationDurationInDays")
                        : s.initialDurationInDaysByDosageId[dosage.Id],
                    arg1: medicationModel.Model.Id,
                    arg2: dosage.Id,
                    arg3: PrescriptionMedicationTextBoxes.DosageDuration,
                    unit: MedicationDosageDisplayModeDurationUnit.Days
                });
            });
        });
    }
}

export { MedicationListType, PatientPrescriptionTab };
